import React, { useState, useEffect } from "react";
import { Box, Typography, Avatar, useTheme, useMediaQuery } from "@mui/material";
import { Images } from "../../../utils/Images";
import { getRecentDonorsByCampaign } from "../../../api/services/donorService";

const getAmountLabel = (amount, currency) => {
  if (!amount && amount !== 0) {
    return "";
  }

  const numericAmount = Number(amount);
  if (isNaN(numericAmount)) {
    return "";
  }

  const currencySymbol = currency === "USD" ? "$" : "₹";
  const formattedAmount = numericAmount.toLocaleString("en-IN", {
    maximumFractionDigits: 0,
  });

  return `${currencySymbol}${formattedAmount}`;
};

const DonationCard = ({ donor }) => (
  <Box
    sx={{
      background: "#fff",
      borderRadius: "12px",
      boxShadow: "0px 0px 13px 0px #464FAF1A",
      border: "1px solid #EAEAF0",
      padding: "9px",
      display: "flex",
      alignItems: "center",
      gap: "12px",
      height: "68px", // Fixed height to prevent resizing
      minWidth: {
        xs: "220px", // Minimum width on mobile
        sm: "240px", // Minimum width on tablet
        md: "260px", // Minimum width on desktop
      },
      mx: { xs: 1, sm: 1.5, md: 2 }, // Margin on sides for spacing between cards
      boxSizing: "border-box", // Include padding in width/height calculation
      flexShrink: 0, // Prevent shrinking
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        width: "100%",
        overflow: "hidden", // Prevent content overflow
      }}
    >
      <Avatar
        src={donor.avatar || undefined}
        alt={donor.name}
        sx={{ width: 50, height: 50, flexShrink: 0 }}
      >
        {donor.name?.charAt(0)?.toUpperCase() || ""}
      </Avatar>
      <Box
        sx={{ 
          display: "flex", 
          flexDirection: "column", 
          flex: 1,
          overflow: "hidden", // Prevent content overflow
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            color: "#4A4A49",
            fontSize: "14px",
            fontWeight: "500",
            whiteSpace: "nowrap", // Prevent text wrapping
            overflow: "hidden",
            textOverflow: "ellipsis", // Show ellipsis for overflow
          }}
        >
          {donor.name || "Anonymous"}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "#1D1D1B",
            fontWeight: 700,
            fontSize: "14px",
            whiteSpace: "nowrap", // Prevent text wrapping
            overflow: "hidden",
            textOverflow: "ellipsis", // Show ellipsis for overflow
          }}
        >
          Donated -{" "}
          <Typography
            component="span"
            sx={{ color: "#8EA946", fontWeight: 700 }}
          >
            {getAmountLabel(donor.amount, donor.currency)}
          </Typography>
        </Typography>
      </Box>
    </Box>
  </Box>
);

const DonationSlider = ({ campaignId }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg")); // 1200px+
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchRecentDonors = async () => {
      if (!campaignId) {
        setDonations([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const donors = await getRecentDonorsByCampaign(campaignId);
        if (isMounted) {
          // Use all donors without any filter
          setDonations(donors || []);
        }
      } catch (error) {
        console.error("Error fetching recent donors:", error);
        if (isMounted) {
          setDonations([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchRecentDonors();

    return () => {
      isMounted = false;
    };
  }, [campaignId]);

  // Show loading state
  if (loading) {
    return null;
  }

  // Hide entire section if no donations
  if (donations.length === 0) {
    return null;
  }

  // Responsive (below lg): always auto-scroll with infinite animation (like news logo on home).
  // Desktop: only duplicate when 6+ donors so we don't show 4 cards when API returns 2.
  const duplicateForLoop = !isDesktop || donations.length >= 6;

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "1440px",
        margin: "0 auto",
        padding: "20px 0",
        marginBlock: "20px",
        overflow: "hidden",
        position: "relative",
        '&::before, &::after': {
          content: '""',
          position: "absolute",
          top: 0,
          width: "50px",
          height: "100%",
          zIndex: 2,
          pointerEvents: "none",
        },
        '&::before': {
          left: 0,
          background: "linear-gradient(to right, rgba(255,255,255,1), rgba(255,255,255,0))"
        },
        '&::after': {
          right: 0,
          background: "linear-gradient(to left, rgba(255,255,255,1), rgba(255,255,255,0))"
        }
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "max-content",
          ...(duplicateForLoop && {
            animation: "scroll 40s linear infinite",
            '@keyframes scroll': {
              '0%': { transform: 'translateX(0%)' },
              '100%': { transform: 'translateX(-50%)' }
            }
          }),
        }}
      >
        {donations.map((donor, idx) => (
          <DonationCard key={donor.id || `donor-${idx}`} donor={donor} />
        ))}
        {duplicateForLoop && donations.map((donor, idx) => (
          <DonationCard key={`dup-${donor.id || idx}`} donor={donor} />
        ))}
      </Box>
    </Box>
  );
};

export default DonationSlider;